import React from "react";
import PageContainer from "./layout/PageContainer.js";

const About = () => {
    return (
        <PageContainer className="about-page about-us-page">
            <h1 className="about-page__title">About Ahavibes</h1>

            <p className="about-page__lead">
              Stop reacting. Start thinking clearly. A guided framework to examine interpretations, reduce mental noise, and make better decisions.
            </p>

            <section className="about-section">
                <h2 className="about-section__heading">Origins</h2>
                <p className="about-section__p">
                    For years, I journaled almost every day. Page after page, I wrote my thoughts, worries, and reflections. Writing felt like relief, like I was processing life as it happened. But I never went back to those pages. I never examined them closely enough to see the patterns in my own thinking.
                </p>
                <p className="about-section__p">
                    Then life hit me with a crisis that knocked me off balance. In that moment, I realized something uncomfortable: I had been writing for years, but I had never truly understood what my mind was repeating. The same interpretations, the same reactions, the same mental loops — just in different situations.
                </p>
                <p className="about-section__p">
                    Around that time, I had also been thinking about how therapeutic frameworks could be made more accessible. Many people, especially introverts, struggle to clearly express what they are feeling. I kept wondering if there could be a simple tool that helps people examine their thoughts the way a therapist might guide them.
                </p>
                <p className="about-section__p">
                    For a long time, that idea stayed just an idea. Then AI arrived and gave me the breakthrough I needed — access to structured knowledge and the ability to turn reflection into something more actionable. That's when Ahavibes.xyz was born: not as another journaling app, but as a way to help people recognize patterns in their thinking and move from emotional noise to mental clarity.
                </p>
            </section>

            <section className="about-section">
                <h2 className="about-section__heading">What Ahavibes Is</h2>
                <p className="about-section__p">
                    Ahavibes blends intentional reflection with structured cognitive frameworks that help you observe patterns in your thinking — so your inner world becomes clearer and easier to navigate.
                </p>
                <p className="about-section__p">
                    Each day, Ahavibes presents a simple mental structure designed to help you pause and examine what is actually happening inside your mind.
                </p>
                <p className="about-section__p">
                    You are invited to observe:
                </p>
                <ul className="about-section__list">
                    <li>What happened with me? What actually happened (facts)?</li>
                    <li>What interpretation did my mind create?</li>
                    <li>What evidence supports or challenges that interpretation?</li>
                    <li>What response would serve me better?</li>
                </ul>
                <p className="about-section__p">
                    This framework trains the mind to separate facts from stories.
                </p>
                <p className="about-section__p">
                    It transforms scattered thoughts into deliberate thinking.
                </p>
                <p className="about-section__p about-section__highlight">
                    This is not manifesting.
                </p>
                <p className="about-section__p about-section__highlight">
                    This is mental precision.
                </p>
            </section>

            <section className="about-section">
                <h2 className="about-section__heading">How It Works</h2>
                <p className="about-section__p">
                    Sometimes the mind scatters. When it does, Ahavibes gently identifies the type of cognitive drift you may be experiencing — and pairs it with a grounded action step. Because insight without effort changes nothing. Routine and structure create rhythm. Discipline is the human part.
                </p>
                <p className="about-section__p">
                    Five intentional minutes. Clear thought. Clean decisions.
                </p>
                <p className="about-section__p about-section__highlight">
                    Ahavibes is not about escaping reality. It is about stabilizing yourself within it.
                </p>
            </section>

            <section className="about-section">
                <h2 className="about-section__heading">The Pause</h2>
                <p className="about-section__p">
                    A subtle shift inside you ripples outward — not magically, but practically. When one person chooses clarity over reaction, that impact spreads through decisions and behavior. When many do, culture changes.
                </p>
                <ul className="about-section__list">
                    <li>The pause exists.</li>
                    <li>Clarity exists in the space between stimulus and response.</li>
                    <li>We simply choose to use it.</li>
                </ul>
            </section>

            <section className="about-section about-section--cta">
                <h2 className="about-section__heading">Enter Clarity Mode</h2>
                <ul className="about-section__list about-section__list--minimal">
                    <li>Give five minutes.</li>
                    <li>Leave with composure.</li>
                </ul>
            </section>
        </PageContainer>
    );
};

export default About;
